// src/pages/PPMCompletedJobs.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { formatMinutes } from "./timeEntry";

// ---------------- utils ----------------
function cn(...c) { return c.filter(Boolean).join(" "); }
function pad2(n) { return String(n).padStart(2, "0"); }

function isoDate(d) {
  if (!d) return "—";
  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  return `${y}-${m}-${day}`;
}
function tsToDate(ts) {
  if (!ts) return null;
  return ts instanceof Timestamp ? ts.toDate() : new Date(ts);
}

function csv(arr) {
  return arr
    .map((x) => {
      const s = (x ?? "").toString().replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    })
    .join(",");
}

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
function monthOptionLabel(ym) {
  // ym: "YYYY-MM"
  const [y, m] = String(ym).split("-");
  const mi = Number(m);
  return `${MONTHS[mi - 1] || m} - ${m} (${y})`;
}

function buildLastNMonths(n = 18) {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < n; i++) {
    const y = d.getFullYear();
    const m = pad2(d.getMonth() + 1);
    out.push(`${y}-${m}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

// ---------------- UI bits ----------------
function Button({ className, ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white font-semibold",
        "px-4 py-2 rounded-lg transition",
        className
      )}
    />
  );
}
function GhostButton({ className, ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "bg-white hover:bg-gray-50 disabled:bg-gray-50 text-gray-900 font-semibold",
        "px-4 py-2 rounded-lg border border-gray-200 transition",
        className
      )}
    />
  );
}
function Input({ className, ...props }) {
  return (
    <input
      {...props}
      className={cn(
        "w-full rounded-lg border border-gray-200 bg-white px-3 py-2",
        "focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-300",
        className
      )}
    />
  );
}
function Select({ className, children, ...props }) {
  return (
    <select
      {...props}
      className={cn(
        "w-full rounded-lg border border-gray-200 bg-white px-3 py-2",
        "focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-300",
        className
      )}
    >
      {children}
    </select>
  );
}
function Pill({ active, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-3 py-1 rounded-full text-sm font-semibold border transition",
        active
          ? "bg-purple-600 text-white border-purple-600"
          : "bg-white text-gray-800 border-gray-200 hover:bg-gray-50"
      )}
    >
      {label}
    </button>
  );
}

// ---------------- main ----------------
export default function PPMCompletedJobs() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [jobs, setJobs] = useState([]);

  // filters
  const months = useMemo(() => buildLastNMonths(24), []);
  const [filterMode, setFilterMode] = useState("month"); // month | engineer | machine | all
  const [selectedMonth, setSelectedMonth] = useState(months[0] || "");
  const [selectedEngineerId, setSelectedEngineerId] = useState("");
  const [selectedAssetId, setSelectedAssetId] = useState("");
  const [qText, setQText] = useState("");

  // lookup lists (optional but helps dropdowns)
  const [engineers, setEngineers] = useState([]);
  const [assets, setAssets] = useState([]);

  // details drawer
  const [openJob, setOpenJob] = useState(null);

  // load engineers/assets for dropdowns
  useEffect(() => {
    (async () => {
      try {
        const [engSnap, assetSnap] = await Promise.all([
          getDocs(collection(db, "ppmengineers")),
          getDocs(collection(db, "ppmassets")),
        ]);
        setEngineers(
          engSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .sort((a,b) => String(a.name||"").localeCompare(String(b.name||"")))
        );
        setAssets(
          assetSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .sort((a,b) => String(a.name||"").localeCompare(String(b.name||"")))
        );
      } catch {
        // optional lists; ignore load failures
      }
    })();
  }, []);

  // subscribe to completed jobs (scoped by month if desired)
  useEffect(() => {
    setErr("");
    setLoading(true);

    // Firestore requires index for some combos; keep it simple & cheap:
    // - For month view: status==done AND yearMonth==YYYY-MM
    // - For other views: status==done (then filter client-side)
    const base = collection(db, "ppmjobs");

    let qy;
    if (filterMode === "month" && selectedMonth) {
      qy = query(
        base,
        where("status", "==", "done"),
        where("yearMonth", "==", selectedMonth),
        orderBy("dueDate", "asc")
      );
    } else {
      qy = query(
        base,
        where("status", "==", "done"),
        orderBy("dueDate", "desc")
      );
    }

    const unsub = onSnapshot(
      qy,
      (snap) => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setJobs(list);
        setLoading(false);
      },
      (e) => {
        setErr(e?.message || "Failed to load completed jobs.");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [filterMode, selectedMonth]);

  const filtered = useMemo(() => {
    let list = Array.isArray(jobs) ? jobs : [];

    if (filterMode === "month" && selectedMonth) {
      // already server filtered
    } else if (filterMode === "engineer" && selectedEngineerId) {
      list = list.filter(j => String(j.completedByEngineerId || j.assignedEngineerId || "") === String(selectedEngineerId));
    } else if (filterMode === "machine" && selectedAssetId) {
      list = list.filter(j => String(j.assetId || "") === String(selectedAssetId));
    }

    const s = qText.trim().toLowerCase();
    if (s) {
      list = list.filter((j) => {
        const hay = [
          j.assetName,
          j.assetCode,
          j.taskName,
          j.completedByEngineerName,
          j.assignedEngineerName,
          j.yearMonth,
          j.completionNotes,
          j.id,
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(s);
      });
    }

    return list;
  }, [jobs, filterMode, selectedEngineerId, selectedAssetId, qText, selectedMonth]);

  const counts = useMemo(() => {
    const list = Array.isArray(filtered) ? filtered : [];
    const total = list.length;
    const minsSum = list.reduce((acc, j) => acc + (Number(j.timeTakenMins) || 0), 0);
    const hasExceeded = list.some((j) => j.timeTakenExceededLimit === true);
    return { total, minsSum, hasExceeded };
  }, [filtered]);

  const exportCsv = async () => {
  if (filterMode !== "month" || !selectedMonth) {
    alert("Export is only allowed by month to avoid massive exports.");
    return;
  }

  try {
    const colRef = collection(db, "ppmjobs");

    const q = query(
      colRef,
      where("status", "==", "done"),
      where("yearMonth", "==", selectedMonth),
      orderBy("dueDate", "asc")
    );

    const snap = await getDocs(q);

    const data = snap.docs.map(d => d.data());

    const header = [
      "Machine",
      "Asset Code",
      "Task",
      "Due Date",
      "Completed Date",
      "Completed By",
      "Engineer ID",
      "Time (mins)",
      "Notes",
      "Month"
    ];

    const lines = [
      header.join(","),
      ...data.map((j) => {
        return csv([
          j.assetName || "",
          j.assetCode || "",
          j.taskName || "",
          isoDate(tsToDate(j.dueDate)),
          isoDate(tsToDate(j.completedAt)),
          j.completedByEngineerName || j.assignedEngineerName || "",
          j.completedByEngineerId || j.assignedEngineerId || "",
          j.timeTakenMins || 0,
          j.completionNotes || "",
          j.yearMonth || ""
        ]);
      })
    ].join("\n");

    const blob = new Blob([lines], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `ppm_jobs_${selectedMonth}.csv`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

  } catch (err) {
    console.error("Export failed:", err);
    alert("Export failed — likely missing Firestore index.");
  }
};

  return (
    <div className="p-6 max-w-7xl space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Completed PPM Jobs</h1>
          <p className="text-gray-600 mt-1">
            Filter by month / engineer / machine. Click a job to view notes + time taken.
          </p>
        </div>

        <div className="flex gap-2">
          <GhostButton onClick={exportCsv}>
            Export CSV
          </GhostButton>
        </div>
      </div>

      {err ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
          <div className="font-bold">Problem</div>
          <div className="mt-1">{err}</div>
        </div>
      ) : null}

      {/* filters */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          <Pill active={filterMode === "month"} onClick={() => setFilterMode("month")} label="By month" />
          <Pill active={filterMode === "engineer"} onClick={() => setFilterMode("engineer")} label="By engineer" />
          <Pill active={filterMode === "machine"} onClick={() => setFilterMode("machine")} label="By machine" />
          <Pill active={filterMode === "all"} onClick={() => setFilterMode("all")} label="All completed" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <div className="text-sm font-semibold text-gray-800">Search</div>
            <Input value={qText} onChange={(e) => setQText(e.target.value)} placeholder="Machine, task, engineer, notes…" />
          </div>

          {filterMode === "month" ? (
            <div>
              <div className="text-sm font-semibold text-gray-800">Month</div>
              <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                {months.map((ym) => (
                  <option key={ym} value={ym}>{monthOptionLabel(ym)}</option>
                ))}
              </Select>
            </div>
          ) : null}

          {filterMode === "engineer" ? (
            <div>
              <div className="text-sm font-semibold text-gray-800">Engineer</div>
              <Select value={selectedEngineerId} onChange={(e) => setSelectedEngineerId(e.target.value)}>
                <option value="">Select engineer…</option>
                {engineers.map((e) => (
                  <option key={e.id} value={e.id}>{e.name || e.id}</option>
                ))}
              </Select>
            </div>
          ) : null}

          {filterMode === "machine" ? (
            <div>
              <div className="text-sm font-semibold text-gray-800">Machine</div>
              <Select value={selectedAssetId} onChange={(e) => setSelectedAssetId(e.target.value)}>
                <option value="">Select machine…</option>
                {assets.map((a) => (
                  <option key={a.id} value={a.id}>{a.name || a.id}</option>
                ))}
              </Select>
            </div>
          ) : null}

          <div className={cn("rounded-xl border border-gray-200 p-3", filterMode === "month" ? "" : "md:col-span-1")}>
            <div className="text-xs font-semibold text-gray-500">Showing</div>
            <div className="text-lg font-extrabold text-gray-900">{counts.total}</div>
            <div className="text-xs text-gray-500">
              Total mins: {counts.minsSum}{counts.hasExceeded ? " (>=)" : ""}
            </div>
          </div>
        </div>
      </div>

      {/* list */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <div className="text-lg font-extrabold text-gray-900">Jobs</div>
          <div className="text-sm text-gray-600">
            Click a row to see full details.
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-gray-600">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-gray-600">No completed jobs match these filters.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filtered.map((j) => {
              const due = tsToDate(j.dueDate);
              const doneAt = tsToDate(j.completedAt);
              const mins = Number(j.timeTakenMins) || 0;

              return (
                <button
                  key={j.id}
                  type="button"
                  onClick={() => setOpenJob(j)}
                  className="w-full text-left p-4 hover:bg-gray-50 transition"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-extrabold text-gray-900 truncate">
                        {j.assetName || "Machine"} — {j.taskName || "Task"}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        Due: <span className="font-semibold">{isoDate(due)}</span>
                        {j.assetCode ? <span className="text-gray-400"> · {j.assetCode}</span> : null}
                        {j.yearMonth ? <span className="text-gray-400"> · {j.yearMonth}</span> : null}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Completed: {isoDate(doneAt)} · By: {j.completedByEngineerName || j.assignedEngineerName || "—"}
                      </div>
                    </div>

                    <div className="shrink-0 text-right">
                      <div className="text-xs font-semibold text-gray-500">Time</div>
                      <div className="text-lg font-extrabold text-purple-700">
                        {j.timeTakenLabel || formatMinutes(mins)}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* details drawer */}
      {openJob ? (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white border border-gray-200 shadow-xl overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-500">Completed job</div>
                <div className="text-xl font-extrabold text-gray-900 truncate">
                  {openJob.assetName || "Machine"} — {openJob.taskName || "Task"}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Due: <span className="font-semibold">{isoDate(tsToDate(openJob.dueDate))}</span>
                  {openJob.yearMonth ? <span className="text-gray-400"> · {openJob.yearMonth}</span> : null}
                </div>
              </div>
              <GhostButton onClick={() => setOpenJob(null)}>Close</GhostButton>
            </div>

            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-xl border border-gray-200 p-3">
                  <div className="text-xs font-semibold text-gray-500">Machine</div>
                  <div className="font-extrabold text-gray-900">{openJob.assetName || "—"}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {openJob.assetCode || openJob.assetId || "—"}
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 p-3">
                  <div className="text-xs font-semibold text-gray-500">Completed by</div>
                  <div className="font-extrabold text-gray-900">
                    {openJob.completedByEngineerName || openJob.assignedEngineerName || "—"}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {openJob.completedByEngineerId || openJob.assignedEngineerId || "—"}
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 p-3">
                  <div className="text-xs font-semibold text-gray-500">Completed at</div>
                  <div className="font-extrabold text-gray-900">{isoDate(tsToDate(openJob.completedAt))}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Status: <span className="font-semibold">{openJob.status || "done"}</span>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 p-3">
                  <div className="text-xs font-semibold text-gray-500">Time taken</div>
                  <div className="font-extrabold text-purple-700 text-xl">
                    {openJob.timeTakenLabel || formatMinutes(openJob.timeTakenMins)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    (stored: {openJob.timeTakenMins || 0} mins)
                  </div>
                  {openJob.timeTakenExceededLimit ? (
                    <div className="mt-2 text-sm font-semibold text-amber-700">
                      Exceeded limit — ≥ 99 days (approximate)
                    </div>
                  ) : null}
                </div>
              </div>

              {openJob.timeTakenNote ? (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-3">
                  <div className="text-sm font-semibold text-amber-900">Time entry note</div>
                  <div className="mt-2 whitespace-pre-wrap text-sm text-amber-950">
                    {openJob.timeTakenNote}
                  </div>
                </div>
              ) : null}

              <div className="rounded-xl border border-gray-200 p-3">
                <div className="text-sm font-semibold text-gray-800">Completion notes</div>
                <div className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">
                  {openJob.completionNotes || "—"}
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 p-3">
                <div className="text-sm font-semibold text-gray-800">Job ID</div>
                <div className="text-xs font-mono text-gray-600 mt-1">{openJob.id}</div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
              <Button onClick={() => window.print()}>Print</Button>
              <GhostButton onClick={() => setOpenJob(null)}>Close</GhostButton>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
